import {
  Box,
  Button,
  Card,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import AgGridCustomTable from "../../../../util/component/AgGridCustomTable";
import ETollActions from "../../service/eToll.actions";
import ETollFilterAndSearch from "./ETollFilterAndSearch";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import EmptyState from "../../../../util/component/EmptyState";
import FileSaver from "file-saver";
import { setSnackbar } from "../../../../util/state/globalAction";
import { snackbarTypes } from "../../../../util/state/globalVariables";
import { GlobalContext } from "../../../../util/state/GlobalContextProvider";
import CustomLoader from "../../../../util/component/CustomLoader";
import EmptyOrErrorInput from "./EmptyOrErrorInput";
import dayjs from "dayjs";
import { ContextOperation } from "../../context/OperationContext";
import EnumChangeName from "../common/EnumChangeName";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import useSnackbar from "../../hooks/useSnackbar";
import SectionHeader from "../../../../util/component/SectionHeader";
import AddIcon from "@mui/icons-material/Add";
import useToggle from "../../hooks/useToggle";
import CreateEToll from "./CreateEToll";
import { useHasScope } from "../../../../config/globalConfig";
import { saveFileFromResponse } from "../../utils";
import { AxiosError } from "axios";
import UploadSuccessModal from "./UploadSuccessModal";
import AgGridOperationTable from "../common/AgGridOperationTable";
import { OperationScope } from "../../config";

const EToll = () => {
  const hasScope = useHasScope;
  const [eTollData, setETollData] = useState<any>({
    number: "",
    vin: "",
    plate: "",
  });
  const { tenant } = useContext(ContextOperation);
  const [openCreateModal, toggleCreateModal] = useToggle();
  const [resetForm, setResetForm] = useState<boolean>(false);
  const [refreshByErrorModal, setRefreshByErrorModal] =
    useState<boolean>(false);
  const [firstTimeRender, setIsFirstTimeRender] = useState<boolean>(true);
  const [loading, setIsLoading] = useState<boolean>(false);
  const [openErrorModal, setOpenErrorModal] = useState<boolean>(false);
  const [getData, setGetData] = useState<any>([]);
  const [numberOfData, setNumberOfData] = useState<number>(-1);
  const [filterCount, setFilterCount] = useState<number>(0);
  const [status, setStatus] = useState<number>(0);
  const [title, setTitle] = useState<string>("");
  const [body, setBody] = useState<string>("");
  const { dispatchGlobalData } = useContext(GlobalContext);
  const eTollActions = ETollActions();
  const { t } = useTranslation(["operation"]);
  const snackbar = useSnackbar();
  const { i18n } = useTranslation();

  const [isOpenSuccessModal, toggleSuccessModal] = useToggle();

  const ENABLE_ADD_ETOLL = false; //operation not permitted in eToll

  const params: any = {};
  for (const key of Object.keys(eTollData)) {
    if (eTollData[key] !== "") {
      if (key === "number") params[key] = eTollData[key];
      else params[key] = eTollData[key].toUpperCase();
    }
  }

  const columnDefs: any = [
    {
      field: "plate",
      headerName: t("eToll.plate"),
      minWidth: 150,
    },
    {
      field: "vin",
      headerName: t("eToll.vin"),
      minWidth: 150,
      cellRenderer: (params: any) => (
        <Tooltip title={!!params?.data?.vin ? params?.data?.vin : ""}>
          <span>{!!params?.data?.vin ? params?.data?.vin : ""}</span>
        </Tooltip>
      ),
    },
    {
      field: "number",
      headerName: t("eToll.number"),
      minWidth: 150,
    },
    {
      field: "status",
      headerName: t("eToll.status"),
      minWidth: 150,
      cellRenderer: (cellValues: any) =>
        EnumChangeName().activateType(
          cellValues.data?.status !== undefined ? cellValues.data?.status : ""
        ),
    },
    {
      field: "linkDatetime",
      headerName: t("eToll.linkDatetime"),
      minWidth: 200,
      cellRenderer: (cellValues: any) =>
        dateColumn(cellValues.data?.linkDatetime),
    },
    {
      field: "unlinkDatetime",
      headerName: t("eToll.unlinkDatetime"),
      minWidth: 200,
      cellRenderer: (cellValues: any) =>
        dateColumn(cellValues.data?.unlinkDatetime),
    },
    {
      field: "creationDatetime",
      headerName: t("eToll.creationDatetime"),
      minWidth: 200,
      cellRenderer: (cellValues: any) =>
        dateColumn(cellValues.data?.creationDatetime),
    },
  ];

  const resetData = () => {
    setFilterCount(0);
    setETollData({
      number: "",
      vin: "",
      plate: "",
    });
    setGetData([]);
    setStatus(0);
    setNumberOfData(-1);
  };

  const getETollData = () => {
    if (!firstTimeRender) {
      setIsLoading(true);

      for (const key of Object.keys(params)) {
        if (typeof params[key] === 'string') {
          params[key] = params[key].trim();
        }
      }

      eTollActions
        .getEToll(tenant, params)
        .then((response: any) => {
          setGetData(managerData(response));
          setNumberOfData(response.data.etollEquipList.length);
          setStatus(0);
          setIsLoading(false);
        })
        .catch((error: any) => {
          if (error.response) {
            console.log(error.response, "enter error.response");
            console.log("enter error");
            setStatus(error.response.status);
            setGetData([]);
            setOpenErrorModal(true);
            setTitle(t("eToll.wrongPlateOrVinOrNumber"));
            setBody(t("eToll.incorrectVehicle"));
          } else if (error.request) {
            dispatchGlobalData({
              type: setSnackbar,
              payload: {
                snackbar: {
                  message: t("networkErrorMessage"),
                  type: snackbarTypes.error,
                  show: true,
                  autoClose: true,
                },
              },
            });
            setIsLoading(false);
          } else {
          }
          setIsLoading(false);
        });
    }
  };

  const dateColumn = (cellValues: any) => {
    return (
      <Box component={"span"}>
        {!!cellValues && dayjs(`${cellValues}`).format("DD/MM/YYYY")}
      </Box>
    );
  };

  const managerData = (response: any) => {
    if (response !== undefined) {
      let rows: any[] = [];
      response.data.etollEquipList.forEach((element: any) => {
        let row = {
          id: !!element.id ? element.id : "",
          plate: !!element.plate ? element.plate : "",
          vin: !!element.vin ? element.vin : "",
          number: !!element.number ? element.number : "",
          description: !!element.description ? element.description : "",
          status: !!element.status ? element.status : "",
          supplierDescription: !!element.supplierDescription
            ? element.supplierDescription
            : "",
          supplierCode: !!element.supplierCode ? element.supplierCode : "",
          linkDatetime: !!element.linkDatetime
            ? element.linkDatetime
            : null,
          unlinkDatetime: !!element.unlinkDatetime
            ? element.unlinkDatetime
            : null,
          creationDatetime: !!element.createDatetime
            ? element.createDatetime
            : null,
        };
        rows.push(row);
      });
      return rows;
    }
  };

  const exportDataETollData = () => {
    setIsLoading(true);
    eTollActions
      .exportAllETollData({ ...params, tenant: tenant })
      .then((response: any) => {
        setIsLoading(false);
        const filename =
          response?.headers["content-disposition"].split("filename=")[1];
        var blob = new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        FileSaver.saveAs(blob, filename);
      });
  };

  const onImageChange = (event: any) => {
    if (event.target.files && event.target.files[0]) {
      let img = event.target.files[0];
      const formData = new FormData();
      formData.append("file", img);
      if (img) {
        setIsLoading(true);
        eTollActions
          .uploadETollFile(tenant, i18n.resolvedLanguage, formData)
          .then(
            (response) => {
              setIsLoading(false);
              Object.keys(params).length === 0
                ? resetData()
                : setResetForm(!resetForm);

              toggleSuccessModal();
            }
          )
          .catch((error: AxiosError<any, any>) => {
            if (
              error.response?.status === 400 &&
              error.response?.data.subCode === 7
            ) {
              setOpenErrorModal(true);
              setTitle(t("eToll.uploadFile.title"));
              setBody(error.response.data.status);
            } else if (error.response?.status === 400) {
              snackbar.show("error", t("eToll.uploadFile.errorMsg400"));
            } else {
              snackbar.show("error", t("eToll.uploadFile.errorMsg"));
            }
            setIsLoading(false);
          });
      }
      event.target.value = null;
    }
  };

  const noEToll = () => (
    <EmptyState
      title={t("eToll.noETollTitle")}
      content={t("eToll.noETollContent")}
    />
  );

  const noRfidRecord = () => (
    <EmptyState
      title={t("eToll.noRfidRecordTilte")}
      content={t("eToll.noRfidRecordContent")}
    />
  );

  const downloadTemplate = () => {
    setIsLoading(true);
    eTollActions.downloadTemplate().then(
      (response: any) => {
        setIsLoading(false);
        saveFileFromResponse(response);
      },
      error => {
        snackbar.show("error", t("eToll.downloadError"));
      }
    );
  };

  useEffect(() => {
    setFilterCount(Object.keys(params).length);
    getETollData();
  }, [resetForm]);

  useEffect(() => {
    getETollData();
  }, [refreshByErrorModal]);

  return (
    <Box>
      <SectionHeader
        title={t("eToll.titleFirst") + " " + t("eToll.titleSecond")}
      />
      <ETollFilterAndSearch
        resetData={resetData}
        eTollData={eTollData}
        setETollData={setETollData}
        resetForm={resetForm}
        setResetForm={setResetForm}
        setIsFirstTimeRender={setIsFirstTimeRender}
      />
      <Box className="carBrandOuterTableBox">
        <Card>
          <Box
            minHeight={46}
            pr={1}
            display="flex"
            justifyContent={"flex-end"}
            alignItems="center"
          >
            {useHasScope(OperationScope.UpdateVehicleETollScope) &&
              (
                <>
                  <Box mr={2}>
                    <Button
                      variant="outlined"
                      onClick={downloadTemplate}
                      startIcon={<FileDownloadIcon />}
                    >
                      <Typography color={"accent"} fontWeight={700}>
                        {t(
                          "transportSupplierPriceList.templateButton.downloadTemplate"
                        )}
                      </Typography>
                    </Button>
                  </Box>

                  <Box className="App">
                    <label htmlFor="upload-equipment">
                      <input
                        style={{ display: "none" }}
                        id="upload-equipment"
                        onChange={onImageChange}
                        name="upload-equipment"
                        type="file"
                      />
                      <Button
                        aria-label="toggle filter"
                        startIcon={<FileUploadIcon />}
                        component="span"
                        variant="contained"
                        className="filtersBtn uploadDownloadBtns"
                      >
                        <Typography color={"accent"} fontWeight={700}>
                          {t("eToll.uploadFile.uploadEToll")}
                        </Typography>
                      </Button>
                    </label>
                  </Box>
                </>
              )}
            <Tooltip title={t("tooltipTitle.download")}>
              <IconButton
                color="accent"
                disabled={numberOfData <= 0 || status === 404 ? true : false}
                onClick={exportDataETollData}
              >
                <FileDownloadIcon />
              </IconButton>
            </Tooltip>
            {useHasScope(OperationScope.UpdateVehicleETollScope) && ENABLE_ADD_ETOLL &&
              (
                <Tooltip title={t("tooltipTitle.add")}>
                  <span>
                  <IconButton
                    color="accent"
                    disabled={filterCount === 0 ? true : false}
                    onClick={toggleCreateModal}
                  >
                    <AddIcon />
                  </IconButton>
                  </span>
                </Tooltip>
              )}
          </Box>
        </Card>

        <Box>
          {numberOfData === -1 ? (
            <Box>
              <Box>
                <AgGridOperationTable
                  columnDefs={columnDefs}
                  rowData={getData}
                  rowStyle={{ width: "150%" }}
                  totalElements={getData?.length}
                  rowModelType="clientSide"
                />

              </Box>
            </Box>
          ) : (
            <Box>
              <AgGridOperationTable
                columnDefs={columnDefs}
                rowData={getData}
                rowStyle={{ width: "150%" }}
                totalElements={getData?.length}
                rowModelType="clientSide"
              />

            </Box>
          )}
        </Box>
        {loading && <CustomLoader loading={loading} />}

        {openErrorModal && (
          <EmptyOrErrorInput
            setOpenErrorModal={setOpenErrorModal}
            openErrorModal={openErrorModal}
            title={title}
            body={body}
            setRefreshByErrorModal={setRefreshByErrorModal}
            refreshByErrorModal={refreshByErrorModal}
            filterCount={filterCount}
          />
        )}
        {openCreateModal && (
          <CreateEToll
            open={openCreateModal}
            onClose={toggleCreateModal}
            resetForm={resetForm}
            setResetForm={setResetForm}
          />
        )}
      </Box>

      <UploadSuccessModal
        open={isOpenSuccessModal}
        onClose={toggleSuccessModal}
      />
    </Box>
  );
};

export default EToll;
